import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errMsg, pkr, dateRange } from "@/lib/api";

const act = async (fn, ok, reload) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } };

export function PartnersPanel({ items, api, reload }) {
  const [notes, setNotes] = useState({});
  return (
    <div className="space-y-4" data-testid="admin-partners">
      {items.map((p) => (
        <div key={p.id} className="rounded-xl border border-border bg-white p-5" data-testid={`admin-partner-${p.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><div className="font-semibold">{p.org_name}</div><div className="text-sm text-muted-foreground">{p.email} · {p.city} · {p.partner_roles.join(", ")}</div></div>
            <Badge variant={p.verification_status === "verified" ? "default" : "secondary"}>{p.verification_status}</Badge>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {["kyc", "contract", "bank"].map((t) => {
              const v = p.verifications[t];
              return (
                <div key={t} className="rounded-lg border border-border p-3 text-sm">
                  <div className="flex items-center justify-between"><span className="font-medium uppercase">{t}</span><Badge variant="outline">{v?.status || "not submitted"}</Badge></div>
                  {v && <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">{Object.entries(v.data).map(([k, val]) => <div key={k}>{k}: {String(val)}</div>)}</div>}
                  {v && v.status !== "verified" && (
                    <div className="mt-3 space-y-2">
                      <Input placeholder="Note (optional)" value={notes[`${p.id}${t}`] || ""} onChange={(e) => setNotes({ ...notes, [`${p.id}${t}`]: e.target.value })} data-testid={`verify-note-${p.id}-${t}`} />
                      <div className="flex gap-2">
                        <Button size="sm" className="rounded-full" onClick={() => act(() => api.post(`/admin/partners/${p.id}/verifications/${t}`, { decision: "verified", notes: notes[`${p.id}${t}`] || "" }), "Verified", reload)} data-testid={`verify-approve-${p.id}-${t}`}>Verify</Button>
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(() => api.post(`/admin/partners/${p.id}/verifications/${t}`, { decision: "rejected", notes: notes[`${p.id}${t}`] || "" }), "Rejected", reload)} data-testid={`verify-reject-${p.id}-${t}`}>Reject</Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ListingsPanel({ items, api, reload }) {
  const [filter, setFilter] = useState("in_review");
  const [note, setNote] = useState("");
  const shown = filter === "all" ? items : items.filter((i) => i.status === filter);
  const mod = (id, decision, extra = {}) => act(() => api.post(`/admin/listings/${id}/moderate`, { decision, notes: note, ...extra }), `Listing ${decision}`, reload);
  return (
    <div data-testid="admin-listings">
      <Select value={filter} onValueChange={setFilter}>
        <SelectTrigger className="w-[200px]" data-testid="admin-listing-filter"><SelectValue /></SelectTrigger>
        <SelectContent>{["in_review", "published", "draft", "rejected", "completed", "all"].map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
      </Select>
      <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-white">
        <Table>
          <TableHeader><TableRow><TableHead>Listing</TableHead><TableHead>Partner</TableHead><TableHead>Checklist</TableHead><TableHead>Spotlight</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {shown.length === 0 && <TableRow><TableCell colSpan={5} className="text-muted-foreground">Nothing here.</TableCell></TableRow>}
            {shown.map((l) => {
              const ok = l.compliance.filter((c) => c.ok).length;
              return (
                <TableRow key={l.id} data-testid={`admin-listing-row-${l.id}`}>
                  <TableCell><div className="font-medium">{l.title}</div><div className="text-xs text-muted-foreground">{l.destination} · {l.start_date && dateRange(l.start_date, l.end_date)} · {l.block_count} blocks · <Badge variant="outline">{l.status}</Badge></div></TableCell>
                  <TableCell className="text-sm">{l.partner_name}</TableCell>
                  <TableCell><span className={ok === l.compliance.length ? "text-primary" : "text-destructive"}>{ok}/{l.compliance.length}</span></TableCell>
                  <TableCell><Switch checked={!!l.spotlight} onCheckedChange={(v) => mod(l.id, "spotlight", { spotlight: v })} data-testid={`admin-spotlight-${l.id}`} /></TableCell>
                  <TableCell className="text-right">
                    <Dialog>
                      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full" data-testid={`admin-review-${l.id}`}>Review</Button></DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader><DialogTitle>{l.title}</DialogTitle></DialogHeader>
                        <ul className="space-y-1 text-sm">{l.compliance.map((c) => <li key={c.key} className={c.ok ? "" : "text-destructive"}>{c.ok ? "✓" : "✗"} {c.label}</li>)}</ul>
                        <Textarea placeholder="Moderation note to partner" value={note} onChange={(e) => setNote(e.target.value)} data-testid="moderation-note-input" />
                        <div className="flex flex-wrap gap-2">
                          {l.status === "in_review" && <Button className="rounded-full" onClick={() => mod(l.id, "approve")} data-testid="moderate-approve-btn">Approve & publish</Button>}
                          {l.status === "in_review" && <Button variant="outline" className="rounded-full" onClick={() => mod(l.id, "reject")} data-testid="moderate-reject-btn">Send back</Button>}
                          {l.status === "published" && <Button variant="outline" className="rounded-full" onClick={() => mod(l.id, "unpublish")} data-testid="moderate-unpublish-btn">Unpublish</Button>}
                          {l.status === "published" && <Button variant="outline" className="rounded-full" onClick={() => mod(l.id, "complete")} data-testid="moderate-complete-btn">Mark completed</Button>}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function DisputesPanel({ items, api, reload }) {
  const [amt, setAmt] = useState({});
  const [note, setNote] = useState({});
  return (
    <div className="space-y-4" data-testid="admin-disputes">
      {items.length === 0 && <p className="text-sm text-muted-foreground">No disputes or refund exceptions.</p>}
      {items.map((d) => (
        <div key={d.id} className="rounded-xl border border-border bg-white p-5" data-testid={`admin-dispute-${d.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2"><div className="font-medium">{d.listing?.title} · {d.booking?.code}</div><Badge variant={d.status === "open" ? "secondary" : "outline"}>{d.status}{d.resolution ? ` · ${d.resolution}` : ""}</Badge></div>
          <p className="mt-2 text-sm">“{d.reason}”</p>
          <div className="mt-1 text-xs text-muted-foreground">{d.raised_by_email} · booking {d.booking?.status} · subtotal {pkr(d.booking?.subtotal)} · refunded {pkr(d.booking?.refunded_pkr)}</div>
          {d.status === "open" && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Input type="number" className="w-40" placeholder="Refund PKR" value={amt[d.id] || ""} onChange={(e) => setAmt({ ...amt, [d.id]: e.target.value })} data-testid={`dispute-amount-${d.id}`} />
              <Input className="w-64" placeholder="Resolution note" value={note[d.id] || ""} onChange={(e) => setNote({ ...note, [d.id]: e.target.value })} data-testid={`dispute-note-${d.id}`} />
              <Button size="sm" className="rounded-full" onClick={() => act(() => api.post(`/admin/disputes/${d.id}/resolve`, { resolution: "refund", refund_pkr: Number(amt[d.id] || 0), notes: note[d.id] || "" }), "Refund issued", reload)} data-testid={`dispute-refund-${d.id}`}>Issue refund</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(() => api.post(`/admin/disputes/${d.id}/resolve`, { resolution: "reject", notes: note[d.id] || "" }), "Dispute closed", reload)} data-testid={`dispute-reject-${d.id}`}>Close without refund</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function PayoutsPanel({ items, api, reload }) {
  const decide = (id, decision) => act(() => api.post(`/admin/payouts/${id}/decide`, { decision, notes: "" }), `Payout ${decision}d`, reload);
  return (
    <div className="space-y-4" data-testid="admin-payouts">
      {items.length === 0 && <p className="text-sm text-muted-foreground">No payouts yet. They are created with the first confirmed booking on a listing.</p>}
      {items.map((p) => (
        <div key={p.id} className="rounded-xl border border-border bg-white p-5" data-testid={`admin-payout-${p.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><div className="font-medium">{p.listing?.title}</div><div className="text-xs text-muted-foreground">{p.partner_name} · payout account {p.partner_payout_verified ? "verified" : "NOT verified"}</div></div>
            <Badge variant={p.status === "released" ? "default" : "outline"} className="capitalize" data-testid={`admin-payout-status-${p.id}`}>{p.status}</Badge>
          </div>
          <div className="mt-3 grid gap-2 text-sm sm:grid-cols-4">
            <div>Gross <b>{pkr(p.gross_pkr)}</b></div><div>Commission {p.commission_pct}% <b>{pkr(p.commission_pkr)}</b></div><div>Net <b>{pkr(p.net_pkr)}</b></div><div>Open disputes <b>{p.open_disputes}</b></div>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">Evidence: partner report {p.evidence.partner_report ? "✓" : "✗"} · guest confirmations {p.evidence.guest_confirmations}/{p.evidence.active_bookings} · {p.evidence.verified ? "verified" : "not verified"}</div>
          <div className="mt-2 text-xs text-muted-foreground">{p.allocations.map((a) => `${a.party} ${a.name}: ${a.pct}% (${pkr(a.amount_pkr)})`).join(" · ")}</div>
          <div className="mt-4 flex flex-wrap gap-2">
            {p.status === "eligible" && <Button size="sm" className="rounded-full" onClick={() => decide(p.id, "approve")} data-testid={`payout-approve-${p.id}`}>Approve</Button>}
            {p.status === "approved" && <Button size="sm" className="rounded-full bg-secondary hover:bg-secondary/90" onClick={() => decide(p.id, "release")} data-testid={`payout-release-${p.id}`}>Release funds</Button>}
            {!["released", "blocked"].includes(p.status) && <Button size="sm" variant="outline" className="rounded-full" onClick={() => decide(p.id, "block")} data-testid={`payout-block-${p.id}`}>Block</Button>}
            {p.status === "blocked" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => decide(p.id, "unblock")} data-testid={`payout-unblock-${p.id}`}>Unblock</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function UsersPanel({ data, api, reload, meId }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-white" data-testid="admin-users">
      <Table>
        <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead></TableRow></TableHeader>
        <TableBody>
          {data.users.map((u) => (
            <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
              <TableCell>{u.name}{u.user_id === meId && " (you)"}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
              <TableCell>
                <Select value={u.role} onValueChange={(r) => act(() => api.patch(`/users/${u.user_id}/role`, { role: r }), "Role updated", reload)}>
                  <SelectTrigger className="w-[140px]" data-testid={`admin-user-role-${u.user_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{data.roles.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function AuditPanel({ items }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-white" data-testid="admin-audit">
      <Table>
        <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Who</TableHead><TableHead>Action</TableHead><TableHead>Detail</TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((a) => (
            <TableRow key={a.id}><TableCell className="whitespace-nowrap text-xs">{new Date(a.at).toLocaleString()}</TableCell><TableCell className="text-xs">{a.actor_email} ({a.actor_role})</TableCell><TableCell className="text-sm">{a.action}</TableCell><TableCell className="text-xs text-muted-foreground">{a.target_type} {a.target_id} {a.detail}</TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export const OverviewPanel = ({ o }) => (
  <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border md:grid-cols-4" data-testid="admin-overview">
    {[["Verifications to review", o.partners_pending], ["Listings in review", o.listings_in_review], ["Live listings", o.listings_live], ["Open disputes", o.open_disputes], ["Payouts eligible", o.payouts_eligible], ["Payouts held", o.payouts_held], ["Confirmed bookings", o.bookings_confirmed], ["Escrow held", pkr(o.escrow_held_pkr)]].map(([k, v]) => (
      <div key={k} className="bg-white p-5"><div className="eyebrow text-muted-foreground">{k}</div><div className="mt-2 font-display text-3xl text-primary" data-testid={`overview-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</div></div>
    ))}
    <div className="col-span-2 bg-white p-5 md:col-span-4 text-sm text-muted-foreground">Partner-side builder for any listing: open it from <Link to="/partner" className="text-secondary">Partner workspace</Link> if you also hold a partner profile.</div>
  </div>
);
